// drift-test:no-test-needed: this edit is a type-only import refactor (split Operation into a separate `import type` per verbatimModuleSyntax). Zero runtime behavior change; the 8 existing reducer tests in __tests__/traceMirrorReducer.test.ts already cover snapshot+patch correctness.
// 0062 mirror-architecture frontend reducer. Pairs with the server-side
// trace_snapshot + trace_patch events emitted from DiagCollector
// mutators (orchestrator/internal/subcall/diag.go) + runSingle (initial
// empty snapshot at run start + periodic re-emissions every ~15s).
//
// Wire contract:
//   - trace_snapshot.payload = { trace: models.RunTrace } → replace
//     state.trace entirely. Sent at run start + periodically; the bus
//     truncates its replay buffer on each anchor so subscribers receive
//     (latest anchor + only-post-anchor patches) — no double-application.
//   - trace_patch.payload = { ops: TracePatchOp[] } → apply RFC 6902
//     ops in order via fast-json-patch.
//
// State shape mirrors models.RunTrace from the orchestrator. Drift-
// impossible by construction: adding a field on the Go side makes it
// appear here automatically because the server serializes the
// canonical struct.
//
// Post-Phase-C: this is the SOLE trace source; the legacy hand-mapped
// event-type reducer (traceReducer.ts) was deleted in commit 1250f6f.

import { applyPatch } from "fast-json-patch";
import type { Operation as JsonPatchOperation } from "fast-json-patch";

import type { RunTrace } from "../lib/traceApi";

/**
 * One RFC 6902 JSON Patch op carried on the wire. Mirrors
 * orchestrator/internal/subcall/user_progress.go::TracePatchOp.
 */
export interface TracePatchOp {
  op: "add" | "replace" | "remove";
  path: string;
  value?: unknown;
}

export type TraceMirrorAction =
  | { type: "TRACE_SNAPSHOT"; payload: { trace: RunTrace } }
  | { type: "TRACE_PATCH"; payload: { ops: TracePatchOp[] } }
  | { type: "RESET"; payload: { runId: string } };

export interface TraceMirrorState {
  runId: string;
  // 0062: trace is the canonical RunTrace shape. null until the first
  // snapshot lands (or after RESET before any event arrives). The UI
  // should treat null as "loading" and only render when populated.
  trace: RunTrace | null;
  // patchErrors records ops that failed to apply (e.g. path pointed at
  // a missing parent). Surfaces drift between server-side patch
  // construction + frontend state at validation time. Kept short
  // (last 20) to bound memory.
  patchErrors: string[];
}

export function initMirrorState(runId: string): TraceMirrorState {
  return { runId, trace: null, patchErrors: [] };
}

export function traceMirrorReducer(
  state: TraceMirrorState,
  action: TraceMirrorAction,
): TraceMirrorState {
  switch (action.type) {
    case "RESET":
      return initMirrorState(action.payload.runId);

    case "TRACE_SNAPSHOT": {
      // Snapshot ALWAYS replaces state with the latest server-emitted
      // baseline. The server's periodic snapshot emitter (15s interval)
      // makes anchor freshness the canonical source of truth — the bus
      // truncates its replay buffer on each anchor publish, so post-
      // anchor patches that the client has already applied are NOT
      // duplicated on top of the new snapshot. On EventSource
      // reconnect, the brief "rewind then catch up via post-anchor
      // patches" is a tolerable visual blip; state converges quickly
      // and reflects server-canonical state by design.
      //
      // Earlier "idempotent snapshot" semantics (ignore re-deliveries)
      // was incompatible with the periodic-emit architecture: it froze
      // state at the first anchor's empty baseline and prevented
      // subsequent anchors from delivering accumulated stage/sub-call
      // progress to slow-sub-drop-then-reconnect subscribers.
      return {
        ...state,
        trace: action.payload.trace,
        patchErrors: [], // snapshot resets any prior patch-failure state
      };
    }

    case "TRACE_PATCH": {
      if (state.trace === null) {
        // Patch arrived before any snapshot — defensive: record as a
        // pending-on-snapshot warning rather than crash. In practice
        // the runner emits an empty snapshot at run start before any
        // mutator fires, so this branch shouldn't be hit; but late-
        // subscribe race + bounded replay buffer could lose the
        // snapshot in extreme cases.
        return {
          ...state,
          patchErrors: bumpErrors(
            state.patchErrors,
            `patch received before snapshot (${action.payload.ops.length} ops dropped)`,
          ),
        };
      }
      // fast-json-patch mutates the document by default; pass mutate=false
      // so we get a new document and React picks up the change.
      try {
        const result = applyPatch(
          state.trace,
          action.payload.ops as JsonPatchOperation[],
          /* validateOperation */ false,
          /* mutateDocument */ false,
        );
        return {
          ...state,
          trace: result.newDocument as RunTrace,
        };
      } catch (e) {
        // Op failed (path missing parent, wrong type, etc.). Record +
        // continue. The next snapshot will resync.
        const msg = e instanceof Error ? e.message : String(e);
        return {
          ...state,
          patchErrors: bumpErrors(
            state.patchErrors,
            `patch apply failed: ${msg}`,
          ),
        };
      }
    }
  }
}

// bumpErrors appends + caps at 20 entries (oldest dropped). Tight cap
// because patch errors are bugs to fix, not a normal occurrence.
function bumpErrors(prev: string[], msg: string): string[] {
  const next = [...prev, msg];
  return next.length > 20 ? next.slice(-20) : next;
}
